import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, TextField, MenuItem, Typography, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Avatar,
  Tabs, Tab, Divider, Stack, Card, CardContent, IconButton, Tooltip,
  LinearProgress, Stepper, Step, StepLabel, StepConnector,
  stepConnectorClasses, Alert, AlertTitle, InputAdornment, CircularProgress,
} from "@mui/material";
import {
  Search, Refresh, Visibility, CheckCircle, Cancel, LocalShipping,
  Inventory, AttachMoney, HourglassEmpty, CloudUpload, DeleteOutline,
  CalendarToday, Phone, Email, LocationOn, Receipt, FilterList,
  PlayArrow,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import { getImageFullUrl, pageRoute } from "../utils/common";
import { useNavigate } from "react-router-dom";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_FLOW = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled", 'returned'];

const STATUS_CONFIG = {
  pending: { color: "warning", label: "Pending", icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  confirmed: { color: "info", label: "Confirmed", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  processing: { color: "secondary", label: "Processing", icon: <Inventory sx={{ fontSize: 14 }} /> },
  shipped: { color: "primary", label: "Shipped", icon: <LocalShipping sx={{ fontSize: 14 }} /> },
  delivered: { color: "success", label: "Delivered", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  cancelled: { color: "error", label: "Cancelled", icon: <Cancel sx={{ fontSize: 14 }} /> },
  returned: { color: "error", label: "Returned", icon: <Cancel sx={{ fontSize: 14 }} /> },
};

const PAYMENT_CONFIG = {
  paid: { color: "success", label: "Paid" },
  pending: { color: "warning", label: "Pending" },
  failed: { color: "error", label: "Failed" },
  refunded: { color: "info", label: "Refunded" },
};

const TIMELINE_STEPS = [
  { label: "Order Placed", field: "createdAt" },
  { label: "Confirmed", field: "confirmedAt" },
  { label: "Processing", field: "processingAt" },
  { label: "Shipped", field: "shippedAt" },
  { label: "Delivered", field: "deliveredAt" },
];

// ─── Styled ───────────────────────────────────────────────────────────────────

const StyledConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 17 },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.divider,
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const StepIconRoot = styled("div")(({ theme, ownerState }) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 700,
  border: `2px solid ${ownerState.completed || ownerState.active ? theme.palette.primary.main : theme.palette.divider}`,
  background: ownerState.completed
    ? theme.palette.primary.main
    : ownerState.active
      ? alpha(theme.palette.primary.main, 0.1)
      : theme.palette.background.paper,
  color: ownerState.completed
    ? "#fff"
    : ownerState.active
      ? theme.palette.primary.main
      : theme.palette.text.disabled,
  transition: "all .25s",
}));

function StepIcon({ active, completed, icon }) {
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? <CheckCircle sx={{ fontSize: 18 }} /> : icon}
    </StepIconRoot>
  );
}

const AccentCard = styled(Card)(({ theme, accentcolor }) => ({
  borderRadius: 14,
  border: `1px solid ${theme.palette.divider}`,
  borderTop: `4px solid ${accentcolor}`,
  boxShadow: "none",
  transition: "transform .2s, box-shadow .2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 24px ${alpha(accentcolor, 0.18)}`,
  },
}));

const HoverRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "background .15s",
  "&:hover": { background: alpha(theme.palette.primary.main, 0.04) },
  "&:last-child td": { border: 0 },
}));

const UploadZone = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.light}`,
  borderRadius: 12,
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  background: alpha(theme.palette.primary.main, 0.03),
  transition: "all .2s",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.07),
    borderColor: theme.palette.primary.main,
  },
  width: '100%'
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    })
    : null;

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    })
    : "—";

const parseJSON = (str, fallback = []) => {
  try { return typeof str === "string" ? JSON.parse(str) : str || fallback; }
  catch { return fallback; }
};

const initials = (name = "") =>
  (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

// ─── Section Title ────────────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      fontWeight={700}
      letterSpacing={1.2}
      sx={{ mb: 1, display: "block" }}
    >
      {children}
    </Typography>
  );
}

// ─── Order Timeline ───────────────────────────────────────────────────────────

function OrderTimeline({ order }) {
  const curIdx = STATUS_FLOW.indexOf(order.order_status);
  const cancelled = order.order_status === "cancelled" || order.order_status === "returned";

  return (
    <Box>
      <SectionTitle>Order Journey</SectionTitle>

      {cancelled && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle>
            Order {order.order_status === "cancelled" ? "Cancelled" : "Returned"}
          </AlertTitle>
          {order.cancelReason || "No reason provided."}
          {(order.cancelledAt || order.returnedAt) && (
            <Typography variant="caption" display="block" mt={0.5}>
              At: {fmt(order.cancelledAt || order.returnedAt)}
            </Typography>
          )}
        </Alert>
      )}

      <Stepper
        activeStep={cancelled ? -1 : curIdx}
        alternativeLabel
        connector={<StyledConnector />}
        sx={{ px: 0 }}
      >
        {TIMELINE_STEPS.map((step, i) => {
          const date = order[step.field];
          const completed = !cancelled && i < curIdx;
          const active = !cancelled && i === curIdx;
          return (
            <Step key={step.label} completed={completed}>
              <StepLabel
                StepIconComponent={(props) => <StepIcon {...props} icon={i + 1} />}
              >
                <Typography
                  variant="caption"
                  fontWeight={active ? 700 : 500}
                  color={active ? "primary.main" : "text.primary"}
                >
                  {step.label}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color={date ? "text.secondary" : "text.disabled"}
                  sx={{ fontSize: 10, mt: 0.3, lineHeight: 1.4 }}
                >
                  {date ? fmt(date) : "—"}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Date Breakdown Table */}
      <Box sx={{ mt: 3 }}>
        <SectionTitle>Date Breakdown</SectionTitle>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { event: "Order Placed", date: order.createdAt },
                { event: "Confirmed", date: order.confirmedAt },
                { event: "Processing", date: order.processingAt },
                { event: "Shipped", date: order.shippedAt },
                { event: "Delivered", date: order.deliveredAt },
                { event: "Cancelled", date: order.cancelledAt },
                { event: "Returned", date: order.returnedAt },
              ]
                .filter((r) => r.date)
                .map((r) => (
                  <TableRow key={r.event}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarToday sx={{ fontSize: 13, color: "text.disabled" }} />
                        <Typography variant="body2" fontWeight={500}>{r.event}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{fmt(r.date)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<CheckCircle sx={{ fontSize: "14px !important" }} />}
                        label="Done"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Notes */}
      {(order.notes || order.adminNote) && (
        <Box sx={{ mt: 2 }}>
          {/* {order.notes && (
            <Alert severity="info" sx={{ mb: 1, borderRadius: 2 }} icon={<Receipt />}>
              <AlertTitle sx={{ fontSize: 13 }}>Order Note</AlertTitle>
              <Typography variant="body2">{order.notes}</Typography>
            </Alert>
          )} */}
          {order.adminNote && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <AlertTitle sx={{ fontSize: 13 }}>Admin Note</AlertTitle>
              <Typography variant="body2">{order.adminNote}</Typography>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const VendorOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [payFilter, setPayFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [updating, setUpdating] = useState(false);

  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  // const [dispatchNote, setDispatchNote]   = useState("");

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    setFetching(true);
    try {
      const res = await apiClient.get("/api/vendor/orders", {
        params: { search, status: statusFilter, page: page + 1, limit: rowsPerPage },
      });
      setOrders(res?.data?.data?.data || []);
      setTotalCount(res?.data?.data?.total || 0);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setFetching(false);
    }
  }, [search, statusFilter, page, rowsPerPage]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // ── Stats ──────────────────────────────────────────────────────────────────

  const stats = useMemo(() => ({
    total: totalCount,
    revenue: orders.reduce((s, o) => s + (o.grand_total || 0), 0),
    paid: orders.filter((o) => o.payment_status === "paid").length,
    pending: orders.filter((o) => o.order_status === "pending").length,
  }), [orders, totalCount]);

  // ── Status Update ──────────────────────────────────────────────────────────

  const updateStatus = async (id, status) => {
    setUpdating(true);
    try {
      const fd = new FormData();
      fd.append("order_status", status);
      // if (dispatchNote) fd.append("note", dispatchNote);
      // mediaFiles.forEach((f, i) => fd.append(`media${i}`, f));

      await apiClient.put(`/api/vendor/orders/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(`Order marked as "${status}"`);
      setMediaFiles([]);
      setMediaPreviews([]);
      // setDispatchNote("");
      setSelected((prev) => prev ? { ...prev, order_status: status } : null);
      fetchOrders();
    } catch {
      toast.error("Failed to update order");
    } finally {
      setUpdating(false);
    }
  };

  // ── Media ──────────────────────────────────────────────────────────────────

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);
    setMediaFiles((prev) => [...prev, ...files]);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setMediaPreviews((prev) => [
          ...prev,
          { src: ev.target.result, type: f.type, name: f.name },
        ]);
      reader.readAsDataURL(f);
    });
  };

  const removeMedia = (i) => {
    setMediaFiles((prev) => prev.filter((_, idx) => idx !== i));
    setMediaPreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const parsedItems = useMemo(() => parseJSON(selected?.items, []), [selected]);
  const parsedAddr = useMemo(() => parseJSON(selected?.delivery_address, {}), [selected]);

  const nextActions = useMemo(() => {
    if (!selected) return [];
    switch (selected.order_status) {
      case "pending":
        return [
          { label: "Accept Order", value: "confirmed", color: "primary", icon: <CheckCircle /> },
          // { label: "Reject Order",    value: "cancelled",  color: "error",     icon: <Cancel /> },
        ];
      case "confirmed":
        return [{ label: "Start Processing", value: "processing", color: "secondary", icon: <PlayArrow /> }];
      case "processing":
        return [{ label: "Mark as Shipped", value: "shipped", color: "info", icon: <LocalShipping /> }];
      case "shipped":
        return [{ label: "Mark as Delivered", value: "delivered", color: "success", icon: <CheckCircle /> }];
      default:
        return [];
    }
  }, [selected]);
  const navigate = useNavigate()

  const pageChange = (url) => {
    navigate(url)
  }
  
  const openModal = (order) => {
    pageChange(pageRoute.orderView + order.id)
    // setSelected(order);
    // setActiveTab(0);
    // setMediaFiles([]);
    // setMediaPreviews([]);
    // setDispatchNote("");
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box >

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Order Management</Typography>
          {/* <Typography variant="body2" color="text.secondary" mt={0.3}>
            Manage, dispatch and track your vendor orders
          </Typography> */}
        </Box>
        {/* <Button variant="outlined" startIcon={<Refresh />} onClick={fetchOrders} disabled={fetching}>
          Refresh
        </Button> */}
      </Stack>

      {/* Stat Cards */}
      {/* <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Orders",   value: stats.total,                               accent: "#6366f1", icon: <Receipt /> },
          { label: "Page Revenue",   value: `₹${stats.revenue.toLocaleString("en-IN")}`, accent: "#10b981", icon: <AttachMoney /> },
          { label: "Paid Orders",    value: stats.paid,                                accent: "#3b82f6", icon: <CheckCircle /> },
          { label: "Pending Orders", value: stats.pending,                             accent: "#f59e0b", icon: <HourglassEmpty /> },
        ].map((s) => (
          <Grid item size={{xs:6,md:3}}  key={s.label}>
            <AccentCard accentcolor={s.accent}>
              <CardContent sx={{ p: "16px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.8}>
                    {s.label}
                  </Typography>
                  <Box sx={{ color: s.accent, opacity: 0.75 }}>{s.icon}</Box>
                </Stack>
                <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
              </CardContent>
            </AccentCard>
          </Grid>
        ))}
      </Grid> */}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap">
          <TextField
            size="small" placeholder="Search order / customer…"
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Status</MenuItem>
            {STATUS_FLOW.map((s) => (
              <MenuItem key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Payment" value={payFilter}
            onChange={(e) => { setPayFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Payments</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="pending">Unpaid</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </TextField>
          {(statusFilter || payFilter || search) && (
            <Chip
              icon={<FilterList />}
              label="Clear Filters"
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => { setStatusFilter(""); setPayFilter(""); setSearch(""); }}
              onClick={() => { setStatusFilter(""); setPayFilter(""); setSearch(""); }}
            />
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {fetching && <LinearProgress />}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                {["Order", "Customer", "Amount", "Payment", "Status", "Placed On", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, py: 1.5, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.filter((o) => !payFilter || o.payment_status === payFilter).length === 0 && !fetching ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders
                  .filter((o) => !payFilter || o.payment_status === payFilter)
                  .map((row) => {
                    const items = parseJSON(row.items, []);
                    const sc = STATUS_CONFIG[row.order_status] || STATUS_CONFIG.pending;
                    const pc = PAYMENT_CONFIG[row.payment_status] || PAYMENT_CONFIG.pending;
                    return (
                      <HoverRow key={row.id} onClick={() => openModal(row)}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.orderNumber}</Typography>
                          {/* <Typography variant="caption" color="text.secondary">{row.trackingId?.slice(-10)}</Typography> */}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: 11, bgcolor: "primary.light", color: "primary.dark" }}>
                              {initials(row.customerName)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 130 }}>
                                {row.customerName}
                              </Typography>
                              {/* <Typography variant="caption" color="text.secondary">{row.customerMobile}</Typography> */}
                            </Box>
                          </Stack>
                        </TableCell>
                        {/* <TableCell>
                          <Typography variant="body2">{items.length} item{items.length !== 1 ? "s" : ""}</Typography>
                        </TableCell> */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>₹{row.vendor_total?.toLocaleString("en-IN")}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.payment_type?.toUpperCase()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={pc.label} color={pc.color} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip icon={sc.icon} label={sc.label} color={sc.color} size="small" sx={{ fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CalendarToday sx={{ fontSize: 12, color: "text.disabled" }} />
                            <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                              {fmtDate(row.createdAt)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </HoverRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, np) => setPage(np)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* ══════════════════════════════════════════
          ORDER DETAIL DIALOG
      ══════════════════════════════════════════ */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selected && (
          <>
            <DialogTitle sx={{ pb: 0, pt: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={0.5}>
                    <Typography variant="h6" fontWeight={700}>{selected.orderNumber}</Typography>
                    <Chip
                      icon={STATUS_CONFIG[selected.order_status]?.icon}
                      label={STATUS_CONFIG[selected.order_status]?.label}
                      color={STATUS_CONFIG[selected.order_status]?.color}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={PAYMENT_CONFIG[selected.payment_status]?.label}
                      color={PAYMENT_CONFIG[selected.payment_status]?.color}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  {/* <Typography variant="caption" color="text.secondary" mt={0.4} display="block">
                    {selected.trackingId} • Group: {selected.groupOrderId}
                  </Typography> */}
                </Box>
              </Stack>

              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
              >
                {["Timeline", "Details", "Items", "Dispatch"].map((t) => (
                  <Tab key={t} label={t} sx={{ fontSize: 13, textTransform: "none", fontWeight: 500 }} />
                ))}
              </Tabs>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5, mt: 2 }}>

              {/* Tab 0 – Timeline */}
              {activeTab === 0 && <OrderTimeline order={selected} />}

              {/* Tab 1 – Details */}
              {activeTab === 1 && (
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                      <SectionTitle>Customer</SectionTitle>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: "primary.light", color: "primary.dark", fontWeight: 700 }}>
                          {initials(selected.customerName)}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600}>{selected.customerName}</Typography>
                          {/* <Typography variant="caption" color="text.secondary">ID: …{selected.customerId?.slice(-8)}</Typography> */}
                        </Box>
                      </Stack>
                      <Divider sx={{ mb: 1.5 }} />
                      {[
                        // [<Email sx={{ fontSize: 15 }} />, "Email",     selected.customerEmail],
                        // [<Phone sx={{ fontSize: 15 }} />, "Mobile",    selected.customerMobile],
                        [null, "Method", selected.payment_type?.toUpperCase()],
                        [null, "Payment ID", selected.paymentId],
                      ]
                        .filter(([, , v]) => v)
                        .map(([icon, label, value]) => (
                          <Stack key={label} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.7 }}>
                            {icon && <Box sx={{ color: "text.secondary", mt: 0.2, flexShrink: 0 }}>{icon}</Box>}
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>{label}</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{value}</Typography>
                          </Stack>
                        ))}
                    </Paper>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6 }} >
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                      <SectionTitle>Payment Summary</SectionTitle>
                      {[
                        ["Subtotal", `₹${selected.sub_total?.toLocaleString("en-IN")}`],
                        // ["Tax",       `₹${selected.tax_amount}`],
                        // ["Shipping",  `₹${selected.shipping_cost}`],
                        // ["Coupon",    selected.coupon_code ? `${selected.coupon_code} (-₹${selected.coupon_discount})` : "—"],
                        // ["Discount",  `-₹${selected.total_discount}`],
                      ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                          <Typography variant="body2" color="text.secondary">{k}</Typography>
                          <Typography variant="body2">{v}</Typography>
                        </Stack>
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography fontWeight={700}>Grand Total</Typography>
                        <Typography fontWeight={700} color="primary.main" fontSize={16}>
                          ₹{selected.grand_total?.toLocaleString("en-IN")}
                        </Typography>
                      </Stack>
                      <Box mt={1.5}>
                        <Chip
                          label={PAYMENT_CONFIG[selected.payment_status]?.label}
                          color={PAYMENT_CONFIG[selected.payment_status]?.color}
                          size="small"
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item size={{ xs: 12 }} >
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <SectionTitle>Delivery Address</SectionTitle>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <LocationOn sx={{ color: "text.secondary", mt: 0.3, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {parsedAddr.address_line1}
                            {parsedAddr.landmark ? `, ${parsedAddr.landmark}` : ""}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {[parsedAddr.city, parsedAddr.state, parsedAddr.country, parsedAddr.pincode].filter(Boolean).join(", ")}
                          </Typography>
                          <Chip label={parsedAddr.type || "Home"} size="small" variant="outlined" sx={{ mt: 0.75 }} />
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* <Grid item size={{xs:12,sm:6}} >
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <SectionTitle>Shipping</SectionTitle>
                      {[
                        ["Method",   selected.shipping_method],
                        ["Provider", selected.shippingProvider || "—"],
                        ["Pickup",   selected.pickupType],
                        ["Est. Delivery", selected.estimated_delivery ? fmtDate(selected.estimated_delivery) : "—"],
                      ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                          <Typography variant="body2" color="text.secondary">{k}</Typography>
                          <Typography variant="body2" fontWeight={500}>{v}</Typography>
                        </Stack>
                      ))}
                    </Paper>
                  </Grid> */}

                  {/* <Grid item  size={{xs:12,sm:6}} >
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <SectionTitle>Flags</SectionTitle>
                      {[
                        ["Is Paid",      selected.isPaid],
                        ["Is Delivered", selected.isDelivered],
                      ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.6 }}>
                          <Typography variant="body2" color="text.secondary">{k}</Typography>
                          <Chip label={v ? "Yes" : "No"} color={v ? "success" : "default"} size="small" />
                        </Stack>
                      ))}
                      {selected.returnRequest && (
                        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>Return request pending</Alert>
                      )}
                    </Paper>
                  </Grid> */}
                </Grid>
              )}

              {/* Tab 2 – Items */}
              {activeTab === 2 && (
                <Stack spacing={2}>
                  {parsedItems.map((item, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack direction="row" spacing={2}>
                        {item.thumbnail && (
                          <Box
                            component="img"
                            src={getImageFullUrl(item.thumbnail)}
                            alt={item.productName}
                            onError={(e) => (e.target.style.display = "none")}
                            sx={{ width: 72, height: 72, borderRadius: 2, objectFit: "cover", border: "1px solid", borderColor: "divider", flexShrink: 0 }}
                          />
                        )}
                        <Box flex={1}>
                          <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Box>
                              <Typography fontWeight={600}>{item.productName}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">SKU: {item.sku}</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
                                {item.attributes?.map((a, j) => (
                                  <Chip key={j} label={`${a.attributeName}: ${a.attributeValue}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                                ))}
                              </Stack>
                            </Box>
                            <Box textAlign="right">
                              <Typography fontWeight={700} fontSize={16}>₹{parseFloat(item.price).toLocaleString("en-IN")}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">Qty: {item.qty}</Typography>
                              <Typography variant="body2" color="primary.main" fontWeight={600} display="block">
                                Total: ₹{item.total?.toLocaleString("en-IN")}
                              </Typography>
                              <Chip
                                label={STATUS_CONFIG[item.status]?.label || item.status}
                                color={STATUS_CONFIG[item.status]?.color || "default"}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              {/* Tab 3 – Dispatch */}
              {activeTab === 3 && (
                <Box>
                  {nextActions.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      No dispatch actions available — current status is <strong>{selected.order_status}</strong>.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                      Optionally upload proof images / videos before updating the order status.
                    </Alert>
                  )}

                  <SectionTitle>Upload Proof (Images / Video)</SectionTitle>
                  <UploadZone component="div" sx={{ mb: 2 }}>
                    <input type="file" accept="image/*,video/*" multiple hidden onChange={handleMediaChange} />
                    <CloudUpload sx={{ fontSize: 36, color: "primary.main", mb: 1 }} />
                    <Typography fontWeight={600} color="primary.main">Click to upload files</Typography>
                    <Typography variant="caption" color="text.secondary">JPG, PNG, MP4, MOV — any format</Typography>
                  </UploadZone>

                  {mediaPreviews.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                      {mediaPreviews.map((p, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: "relative", width: 80, height: 80,
                            borderRadius: 2, overflow: "hidden",
                            border: "1px solid", borderColor: "divider",
                          }}
                        >
                          {p.type.startsWith("video") ? (
                            <Box component="video" src={p.src} muted sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <Box component="img" src={p.src} alt={p.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeMedia(i)}
                            sx={{
                              position: "absolute", top: 2, right: 2,
                              bgcolor: "error.main", color: "#fff", p: 0.25,
                              "&:hover": { bgcolor: "error.dark" },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {/* <SectionTitle>Dispatch Note</SectionTitle>
                  <TextField
                    multiline rows={3} fullWidth
                    placeholder="Add a dispatch note, tracking ID or any info for the backend…"
                    value={dispatchNote}
                    onChange={(e) => setDispatchNote(e.target.value)}
                    sx={{ mb: 3 }}
                  /> */}

                  <SectionTitle>Update Order Status</SectionTitle>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    {nextActions.map((action) => (
                      <Button
                        key={action.value}
                        variant="contained"
                        color={action.color}
                        startIcon={updating ? <CircularProgress size={14} color="inherit" /> : action.icon}
                        disabled={updating}
                        onClick={() => updateStatus(selected.id, action.value)}
                        sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                      >
                        {updating ? "Updating…" : action.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setSelected(null)} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default VendorOrderManagement;